import { useGetReducedPdfQuery } from "@services/media";

/**
 * Custom hook to get the optimal media URL based on the priority logic
 * Priority: thumbnailUrl → reduced PDF API → cloudfrontUrl fallback
 *
 * @param cloudfrontUrl - Original cloudfront URL as fallback
 * @param thumbnailUrl - Optional thumbnail URL (highest priority)
 * @param mediaId - Optional media ID for reduced PDF API call
 * @returns Object with optimalUrl and loading state
 */
export const useOptimalMediaUrl = (
  cloudfrontUrl: string,
  thumbnailUrl?: string,
  mediaId?: string,
) => {
  // Use RTK Query to check for reduced PDF availability
  // Skip the query if thumbnailUrl is available (highest priority) or no mediaId
  const {
    data: reducedPdfData,
    isLoading: isReducedPdfLoading,
    isError: isReducedPdfError,
  } = useGetReducedPdfQuery(
    { mediaId: mediaId! },
    { skip: !mediaId || !!thumbnailUrl },
  );

  // React Compiler automatically optimizes this computation
  // Priority 1: Use thumbnailUrl if available
  if (thumbnailUrl) {
    return { optimalUrl: thumbnailUrl, isLoading: false };
  }

  // Priority 2: Use reduced PDF thumbnail if available and query succeeded
  if (reducedPdfData && !isReducedPdfError && reducedPdfData.thumbnailUrl) {
    return { optimalUrl: reducedPdfData.thumbnailUrl, isLoading: false };
  }

  // Priority 3: Fall back to cloudfrontUrl
  // Determine loading state: only loading if waiting for reduced PDF API
  const isLoading = !!mediaId && isReducedPdfLoading;
  return { optimalUrl: cloudfrontUrl, isLoading };
};
